<template>
  <div>
    <table class="table table-borderless">
      <tr>
        <th>{{ lang.deployment_charge }}</th>
        <td>{{ currencySymbol }} {{ deploymentPrice || '-'}}</td>
      </tr>
      <tr v-for="adp in actual_day_prices" v-bind:key="adp.type">
        <th>{{ lang[adp.type.toLowerCase()] }}</th>
        <td>
          <div :class="{'line-through': !!day_prices_discounted}">{{ currencySymbol }} {{ adp.per_day || '-' }} {{ adp.num_of_days ? ('(x' + adp.num_of_days + ')') : '' }}</div>
          <div v-if="!!day_prices_discounted">
            {{ currencySymbol }} {{ adp.per_day_discounted || '-' }} {{ adp.num_of_days ? ('(x' + adp.num_of_days + ')') : '' }}
          </div>
        </td>
      </tr>
      <tr>
        <th>{{ i18n.app.components.contract.num_of_days }}</th>
        <td>{{ numOfDays || '-'}}</td>
      </tr>
      <tr>
        <th>{{ lang.total_price }}</th>
        <td>
          <div>{{ currencySymbol }} {{ totalPrice || '-'}}</div>
        </td>
      </tr>
      <tr>
        <td colspan="2">
          <span>{{ i18n.app.components.contract.price_overview.footnote }}</span>
        </td>
      </tr>
    </table>
  </div>
</template>

<script>
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { PriceType } from "../../../../models/Price";
import get from 'lodash/get';

dayjs.extend(isBetween);

export default {
  name: "contract-price-overview",
  props: [
    "start_datetime",
    "end_datetime",
    "deployment_prices",
    "is_domestic",
    "day_prices_discounted",
    "day_prices",
    "diffInDays"
  ],
  data() {
    return {
      priceTypes: [],
      pricePerDay: 0,
      numOfDays: 0,
      totalPrice: 0,
      deploymentPrice: 0,
      currencySymbol: env.CURRENCY_SYMBOL,
      lang: i18n.app.components.contract.price_overview,
      actual_day_prices: [],
      startDateTime: new Date(this.start_datetime),
      endDateTime: new Date(this.end_datetime),
    };
  },
  mounted() {
    this.calculatePrices();
  },
  methods: {
    calculatePrices() {
      this.calculateDayPrice();
      this.calculateDeploymentPrice();

      this.totalPrice = Number(this.calculateTotalPrice()).toFixed(2);

      this.$forceUpdate();
    },
    calculateDayPrice() {
      let priceTypes = [];

      if (this.startDateTime && this.endDateTime) {
        const diffTime =
          new Date(this.end_datetime).getTime() - new Date(this.start_datetime).getTime();
        const diffInDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        this.numOfDays = diffInDays > 0 ? diffInDays : 0;

        if (diffInDays <= 7) {
          priceTypes = [
            { type: PriceType.FIRST_WEEK, num_of_days: this.numOfDays }
          ];
        } else if (diffInDays <= 14) {
          priceTypes = [
            { type: PriceType.FIRST_WEEK, num_of_days: 7 },
            { type: PriceType.SECOND_WEEK, num_of_days: this.numOfDays - 7 }
          ];
        } else if (diffInDays >= 15) {
          priceTypes = [
            { type: PriceType.FIRST_WEEK, num_of_days: 7 },
            { type: PriceType.SECOND_WEEK, num_of_days: 7 },
            {
              type: PriceType.AFTER_SECOND_WEEK,
              num_of_days: this.numOfDays - 14
            }
          ];
        }
      } else {
        priceTypes = [
          { type: PriceType.FIRST_WEEK, num_of_days: this.numOfDays }
        ];
      }

      if (!this.is_domestic) {
        for (let i = 0; i < priceTypes.length; i++) {
          priceTypes[i].type = priceTypes[i].type + "_FOREIGN";
        }
      }

      for (let i = 0; i < priceTypes.length; i++) {
        priceTypes[i].per_day = get(this.day_prices, priceTypes[i].type);
        priceTypes[i].per_day_discounted = get(this.day_prices_discounted, priceTypes[i].type);
        priceTypes[i].total = priceTypes[i].per_day * priceTypes[i].num_of_days;
        priceTypes[i].total_discounted = priceTypes[i].per_day_discounted * priceTypes[i].num_of_days;
      }

      this.actual_day_prices = priceTypes;
    },
    calculateDeploymentPrice() {
      const startDateTime = dayjs(new Date(this.start_datetime));
      const morningDate = startDateTime.set("hour", 8).startOf("hour");
      const noonDate = startDateTime.set("hour", 17).startOf("hour");

      const isDuringDay = startDateTime.isBetween(morningDate, noonDate);

      if (isDuringDay)
        this.deploymentPrice = this.deployment_prices[
          this.is_domestic
            ? PriceType.DEPLOYMENT_TT_1
            : PriceType.DEPLOYMENT_FOREIGN_TT_1
        ];
      else
        this.deploymentPrice = this.deployment_prices[
          this.is_domestic
            ? PriceType.DEPLOYMENT_TT_2
            : PriceType.DEPLOYMENT_FOREIGN_TT_2
        ];
    },
    calculateTotalDayPrice() {
      let totalPrice = 0;

      this.actual_day_prices.forEach(dayPrice => {
        totalPrice += dayPrice.total;
      });

      return totalPrice;
    },
    calculateTotalDiscountedDayPrice() {
      let totalPrice = 0;

      this.actual_day_prices.forEach(dayPrice => {
        totalPrice += dayPrice.total_discounted;
      });

      return totalPrice;
    },
    calculateAvgDayPrice() {
      return this.calculateTotalDayPrice() / this.numOfDays;
    },
    calculateTotalPrice() {
      let total = 0;

      const dayPrice = Number(this.day_prices_discounted ? this.calculateTotalDiscountedDayPrice() : this.calculateTotalDayPrice());
      const deploymentPrice = this.deploymentPrice
        ? Number(this.deploymentPrice)
        : 0;

      if (!isNaN(dayPrice)) total += dayPrice;
      if (!isNaN(deploymentPrice)) total += deploymentPrice;

      return total;
    }
  }
};
</script>
